import { Formik, FormikProps } from 'formik';
import React from 'react';

import { FormikContext } from './FormikContext';
import { setIn } from './formUtils';

type ChangedFields<Values> = { [field in keyof Values]?: any };

export type SuperFormikProps<T = any> = {
    validationSchema?: any;
    validate?: any;
    onSubmit?: any;
    render?: any;
    theme?: string;
    initialValues: any;
};

export type ExtendedFormikProps<Values> = FormikProps<Values> & {
    hasErrors?: boolean;
    changedByUser?: ChangedFields<Values>;
    setFieldChanged?(field: keyof Values, isChanged: boolean): void;
    validValues?: Values;
    isTouched?: boolean;
    validateOnMount?: boolean;
    onValueChange?(p: { name: string; value: any }): void;
    onValidValueChange?(p: { name: string; value: any }): void;
    arrayPush(name: string, values: any): void;
    removeArrayItem(name: string, index: number): void;
} & SuperFormikProps;

export class FormWrapper extends React.PureComponent<any, any> {
    _prevValidValues: any;

    _lastValuesMap = {} as any;

    constructor(props) {
        super(props);

        this.state = {
            changedByUser: {},
        };
    }

    _setFieldChanged = (name) => {
        this.setState({
            changedByUser: setIn(this.state.changedByUser, name, true),
        });
    };

    _waitTillValueUpdate = (name, value) => {
        return new Promise((resolve) => {
            this._lastValuesMap[name] = { value, resolve };
            setTimeout(() => {
                resolve();
            }, 10000);
        });
    };

    render() {
        const {
            props: {
                render,
                theme,
                validValuesValidateOnlyChangedByUser,
                ...formikProps
            },
            state: { changedByUser },
        } = this;

        const wrappedRender = (formikRenderProps) => {
            const { setFieldValue, values } = formikRenderProps;

            this._prevValidValues =
                !this._prevValidValues || formikRenderProps.isValid
                    ? values
                    : this._prevValidValues;

            const updatedPromises = Object.keys(this._lastValuesMap).reduce(
                (acc, key: any) => {
                    if (this._lastValuesMap[key].value === values[key]) {
                        acc.push(this._lastValuesMap[key]);
                    }
                    return acc;
                },
                [] as any,
            );

            Object.values(updatedPromises).forEach((item: any) =>
                item.resolve(),
            );

            const renderProps = {
                ...formikRenderProps,
                setFieldValue: async (name, value) => {
                    if (formikRenderProps.values[name] !== value) {
                        setFieldValue(name, value);
                        await this._waitTillValueUpdate(name, value);
                        delete this._lastValuesMap[name];
                    }
                },
                theme,
                hasErrors: !formikRenderProps.isValid,
                changedByUser,
                setFieldChanged: this._setFieldChanged,
                validValues: this._prevValidValues,
                isTouched: !!Object.keys(formikRenderProps.touched).length,

                arrayPush: (arrayFieldName, nextValues) => {
                    const index = values[arrayFieldName].length;

                    Object.keys(nextValues).forEach((key) => {
                        setFieldValue(
                            `${arrayFieldName}.${index}.${key}`,
                            nextValues[key],
                        );
                    });
                },
                removeArrayItem: (arrayFieldName, delIndex) => {
                    const newArr = [...values[arrayFieldName]];
                    newArr.splice(delIndex, 1);
                    setFieldValue(arrayFieldName, newArr);

                    /**
                     * TODO: После удаления поля из формы, нужно отметить эту форму как touched.
                     * Для этого нужно вручную проставлять setFieldChanged/setFieldTouched для удаленного поля.
                     * Также как это делает формик в методе setFieldTouched.
                     */
                },
            };

            return (
                <FormikContext.Provider value={renderProps}>
                    {render(renderProps)}
                </FormikContext.Provider>
            );
        };

        const transformedFormikProps = {
            validateOnBlur: true,
            validateOnChange: false,
            ...formikProps,
            render: wrappedRender,
        };

        // @ts-ignore
        return <Formik {...transformedFormikProps} />;
    }
}
